import { useCallback, useEffect, useState } from 'react';
import axios from '../../../../utils/axios';
import { toastError } from '../../../../utils/toast';
import AdditionallyInsuredFields from '../../../../components/AdditionallyInsured/AdditionallyInsuredFields';
import '../../../../components/AdditionallyInsured/AdditionallyInsured.css';
import {
    hasValidAdditionalInsuredAcceptance,
    normalizeDrawerInsurance,
    normalizeInsurance,
} from '../../../../utils/insuranceHelpers';
import {
    ADDITIONAL_INSURED_CHECKBOX_TEXT,
    ADDITIONAL_INSURED_DISCLAIMER,
    ADDITIONAL_INSURED_LANGUAGE_VERSION,
} from '../../../../constants/additionalInsured';
import { HOST_TERMS_VERSION } from '../../../RegisterPage/hostOnboardingConstants';

function formatAcceptedDate(raw) {
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function emptyEntity() {
    return { legalName: '', address: '' };
}

function entityFromInsurance(insurance) {
    const entity = insurance.legalEntities?.[0];
    return { legalName: entity?.legalName ?? '', address: entity?.address ?? '' };
}

// Tri-state, unlike Step6Insurance's binary 'yes'/'no': the drawer shows this
// election right next to the "required" checklist flag, so defaulting an
// unanswered listing to a visually-selected "No" would misrepresent it as a
// complete opt-out. `null` means genuinely unanswered — neither button shows
// as selected. Trusts the persisted `required` flag directly (rather than
// inferring Yes only from a completed acceptance) so a host who picked Yes
// but hasn't finished entity fields/acceptance yet sees Yes still selected
// on reopen instead of it silently reverting to No.
function initialChoice(insurance) {
    if (insurance.required === true) return 'yes';
    if (insurance.required === false) return 'no';
    // required is null/undefined — no explicit election yet. A pre-existing
    // acceptance (legacy data predating the required flag) implies Yes.
    return insurance.additionalInsuredAcceptance ? 'yes' : null;
}

/**
 * Read-only summary for contexts where insurance can't be edited from the
 * drawer (anything other than a bulk draft today).
 */
function ReadOnlyBody({ ins, acceptanceValid, acceptanceDrifted }) {
    if (acceptanceValid) {
        const dateLabel = formatAcceptedDate(ins.additionalInsuredAcceptance?.acceptedAt);
        return (
            <p className="insured-q-sub">
                <strong>Election accepted{dateLabel ? ` on ${dateLabel}` : ''}.</strong>
                {' '}Record ID: <code>{ins.additionalInsuredAcceptance?.acceptanceRecordId}</code>
            </p>
        );
    }
    if (acceptanceDrifted) {
        return (
            <div className="addl-insured-drift-callout" role="status">
                The Additional Insured wording has been updated since the last acceptance for
                this listing. The host must re-accept via the onboarding wizard before this
                listing can be resubmitted.
            </div>
        );
    }
    return (
        <div className="addl-insured-drift-callout" role="status">
            No Additional Insured acceptance is on file for this listing. Complete via the
            onboarding wizard to submit for review.
        </div>
    );
}

export default function InsuranceSection({
    listing,
    editMode = false,
    dirtyFields,
    setDirtyFields,
    forceEditable = false,
    onPersist,
    onBulkListingsUpdated,
}) {
    const editable = (forceEditable || editMode) && Boolean(onPersist);

    const ins = editable && !forceEditable && dirtyFields?.insurance != null
        ? normalizeInsurance(dirtyFields.insurance)
        : normalizeDrawerInsurance(listing);

    const acceptanceValid = hasValidAdditionalInsuredAcceptance(ins);
    const acceptanceStored = Boolean(ins.additionalInsuredAcceptance);
    const acceptanceDrifted = acceptanceStored && !acceptanceValid;

    const [choice, setChoice] = useState(() => initialChoice(ins));
    const [checked, setChecked] = useState(() => acceptanceValid);
    const [entity, setEntity] = useState(() => entityFromInsurance(ins));
    const [accepting, setAccepting] = useState(false);

    const listingId = listing?._id;
    useEffect(() => {
        // Re-derive the local election/entity draft only when the drawer
        // switches to a different listing — `ins` also changes after every
        // persist, and re-deriving from it there would clobber in-progress
        // typing.
        setChoice(initialChoice(ins));
        setChecked(hasValidAdditionalInsuredAcceptance(ins));
        setEntity(entityFromInsurance(ins));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    // Keeps dirtyFields.insurance from going stale relative to the persisted
    // listing while the drawer is simultaneously in edit mode for other
    // fields — the checklist reads the merged (dirtyFields-aware) view.
    useEffect(() => {
        if (!setDirtyFields) return;
        setDirtyFields((prev) => (prev ? { ...prev, insurance: normalizeDrawerInsurance(listing) } : prev));
    }, [listing, setDirtyFields]);

    // Resolves `false` only on a confirmed save failure — a missing `onPersist`
    // is a no-op, not a failure (the editable UI never renders without one).
    const persistInsurance = useCallback((nextInsurance) => {
        if (!onPersist) return Promise.resolve(true);
        return onPersist(nextInsurance);
    }, [onPersist]);

    const handleSelectYes = useCallback(() => {
        setChoice('yes');
        persistInsurance({ ...ins, required: true });
    }, [ins, persistInsurance]);

    const handleSelectNo = useCallback(() => {
        setChoice('no');
        setChecked(false);
        setEntity(emptyEntity());
        persistInsurance({ ...ins, required: false, legalEntities: [emptyEntity()] });
    }, [ins, persistInsurance]);

    const handleEntityChange = useCallback((field, value) => {
        setEntity((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleEntityBlur = useCallback(() => {
        persistInsurance({ ...ins, required: true, legalEntities: [entity] });
    }, [ins, entity, persistInsurance]);

    const entityFilled = entity.legalName.trim() !== '' && entity.address.trim() !== '';

    const handleAccept = useCallback(async () => {
        if (!listingId || accepting) return;
        setAccepting(true);
        try {
            // The entity-name/address fields feed the acceptance record's
            // hostLegalEntityName fallback server-side — don't record the
            // election against a save the host was never told succeeded.
            const flushed = await persistInsurance({ ...ins, required: true, legalEntities: [entity] });
            if (flushed === false) return;
            await axios.post(`/listings/${listingId}/insurance/additional-insured-acceptance`, {
                languageVersion: ADDITIONAL_INSURED_LANGUAGE_VERSION,
                hostAgreementVersion: HOST_TERMS_VERSION,
            });
            onBulkListingsUpdated?.();
        } catch (err) {
            const status = err?.response?.status;
            const serverError = err?.response?.data?.error;
            if (status === 422 && serverError === 'unsupported acceptance version') {
                setChecked(false);
                toastError('The Additional Insured wording has been updated. Please re-read and re-accept.');
            } else {
                toastError(serverError
                    ? `Couldn't record acceptance: ${serverError}`
                    : 'Failed to record your Additional Insured acceptance. Please try again.');
            }
        } finally {
            setAccepting(false);
        }
    }, [listingId, ins, entity, accepting, persistInsurance, onBulkListingsUpdated]);

    return (
        <div className="host-panel-section">
            <div className="host-panel-section-label">
                Additional Insured
            </div>
            {editable ? (
                <div className="insured-yesno-block">
                    <p className="insured-q">
                        <strong>Do you require ParkPro as additionally insured?</strong>
                    </p>
                    <p className="insured-q-sub">
                        Select Yes if your insurance carrier needs ParkPro added to this
                        property&apos;s liability policy.
                    </p>

                    <div className="insured-yesno" role="group" aria-label="Require additionally insured">
                        <button
                            type="button"
                            className={`insured-yn${choice === 'yes' ? ' on' : ''}`}
                            aria-pressed={choice === 'yes'}
                            disabled={accepting}
                            onClick={handleSelectYes}
                        >
                            Yes
                        </button>
                        <button
                            type="button"
                            className={`insured-yn${choice === 'no' ? ' on' : ''}`}
                            aria-pressed={choice === 'no'}
                            disabled={accepting}
                            onClick={handleSelectNo}
                        >
                            No
                        </button>
                    </div>

                    {choice === 'no' && (
                        <div className="insured-skip-note">
                            ParkPro won&apos;t be named as additionally insured for this property.
                        </div>
                    )}

                    {choice === 'yes' && (
                        <>
                            <AdditionallyInsuredFields
                                legalName={entity.legalName}
                                address={entity.address}
                                disabled={accepting}
                                onLegalNameChange={(legalName) => handleEntityChange('legalName', legalName)}
                                onAddressChange={(address) => handleEntityChange('address', address)}
                                onEntityBlur={handleEntityBlur}
                            />

                            {acceptanceValid && (
                                <p className="insured-q-sub">
                                    <strong>
                                        Election accepted
                                        {formatAcceptedDate(ins.additionalInsuredAcceptance?.acceptedAt)
                                            ? ` on ${formatAcceptedDate(ins.additionalInsuredAcceptance?.acceptedAt)}`
                                            : ''}.
                                    </strong>
                                    {' '}Record ID: <code>{ins.additionalInsuredAcceptance?.acceptanceRecordId}</code>
                                </p>
                            )}

                            <div
                                className="addl-insured-scrollbox"
                                role="region"
                                aria-label="Additional Insured election language"
                                tabIndex={0}
                            >
                                {ADDITIONAL_INSURED_CHECKBOX_TEXT}
                            </div>

                            {acceptanceDrifted && (
                                <div className="addl-insured-drift-callout" role="status">
                                    The Additional Insured wording has been updated since your last
                                    acceptance. Please re-read the language above and re-accept below
                                    to continue.
                                </div>
                            )}

                            <label className="addl-insured-check">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={accepting}
                                    onChange={(e) => setChecked(e.target.checked)}
                                    aria-required="true"
                                />
                                <span className="addl-insured-check-label">
                                    I agree to the Additional Insured Election and Contractual
                                    Requirement above.
                                </span>
                            </label>

                            <p className="addl-insured-disclaimer">
                                {ADDITIONAL_INSURED_DISCLAIMER}
                            </p>

                            <button
                                type="button"
                                className="btn btn-primary insured-accept-btn"
                                disabled={!checked || !entityFilled || accepting}
                                onClick={handleAccept}
                            >
                                {accepting ? 'Recording…' : 'Confirm Additional Insured Election'}
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <ReadOnlyBody ins={ins} acceptanceValid={acceptanceValid} acceptanceDrifted={acceptanceDrifted} />
            )}
        </div>
    );
}
